import { useState, useEffect, useRef } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Edit, Loader2, Trash2, Camera } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useUpdatePatient, useUploadPatientPhoto } from '@/hooks/usePatients'
import { useToast } from '@/hooks/use-toast'
import { Patient } from '@/types'


// Schema adhering to backend validation
// Schema adhering to backend validation
const patientSchema = z.object({
    current_status: z.string().max(100).optional(),

    first_name: z.string().min(1, 'El nombre es obligatorio').max(100),
    last_name: z.string().min(1, 'El apellido es obligatorio').max(100),
    date_of_birth: z.string().refine((date) => new Date(date) <= new Date(), {
        message: 'La fecha de nacimiento no puede ser futura',
    }),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say', 'other']),
    curp: z.string().max(18, 'La CURP tiene máximo 18 caracteres').optional(),
    rfc: z.string().max(13, 'El RFC tiene máximo 13 caracteres').optional(),
    // NOM-004 §5.1 identification-card demographics
    place_of_birth: z.string().max(150).optional(),
    nationality: z.string().max(100).optional(),
    education_level: z.string().max(100).optional(),
    religion: z.string().max(100).optional(),
    coverage_type: z.enum(['private', 'insurance', 'institutional', '']).optional(),
    referring_physician: z.string().max(200).optional(),
    email: z.string().email().optional().or(z.literal('')),
    phone_primary: z.string().max(20).optional(),
    address_line1: z.string().max(200).optional(),
    address_line2: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(100).optional(),
    postal_code: z.string().max(20).optional(),
    blood_type: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', '']).optional(),
    emergency_contacts: z.array(z.object({
        name: z.string().min(1, 'El nombre del contacto es obligatorio').max(100),
        relationship: z.string().min(1, 'La relación es obligatoria').max(50),
        phone: z.string().min(5, 'El número de teléfono es obligatorio').max(20),
        is_primary: z.boolean().default(true)
    })).min(1, 'Se requiere al menos un contacto de emergencia')
})

type PatientFormValues = z.infer<typeof patientSchema>

interface EditPatientDialogProps {
    patient: Patient
    trigger?: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

export function EditPatientDialog({ patient, trigger, open: controlledOpen, onOpenChange: onControlledOpenChange }: EditPatientDialogProps) {
    const [internalOpen, setInternalOpen] = useState(false)
    const open = controlledOpen !== undefined ? controlledOpen : internalOpen
    const setOpen = onControlledOpenChange ?? setInternalOpen
    const { toast } = useToast()
    const { mutate: updatePatient, isPending } = useUpdatePatient()
    const { mutate: uploadPhoto, isPending: isUploadingPhoto } = useUploadPatientPhoto()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const form = useForm<PatientFormValues>({
        resolver: zodResolver(patientSchema),
        defaultValues: {
            first_name: patient.first_name || '',
            last_name: patient.last_name || '',
            date_of_birth: patient.date_of_birth || '',
            gender: (patient.gender as PatientFormValues['gender']) || 'prefer_not_to_say',
            curp: patient.curp || '',
            rfc: patient.rfc || '',
            place_of_birth: patient.place_of_birth || '',
            nationality: patient.nationality || '',
            education_level: patient.education_level || '',
            religion: patient.religion || '',
            coverage_type: (patient.coverage_type as PatientFormValues['coverage_type']) || '',
            referring_physician: patient.referring_physician || '',
            email: patient.email || '',
            phone_primary: patient.phone_primary || '',
            address_line1: patient.address_line1 || '',
            address_line2: patient.address_line2 || '',
            city: patient.city || '',
            state: patient.state || '',
            postal_code: patient.postal_code || '',
            blood_type: (patient.blood_type as PatientFormValues['blood_type']) || '',
            current_status: patient.current_status || '',
            emergency_contacts: (patient.emergency_contacts && patient.emergency_contacts.length > 0)
                ? patient.emergency_contacts
                : [{ name: '', relationship: '', phone: '', is_primary: true }]
        },
    })

    // Reset form when patient changes
    useEffect(() => {
        if (patient) {
            form.reset({
                first_name: patient.first_name || '',
                last_name: patient.last_name || '',
                date_of_birth: patient.date_of_birth || '',
                gender: (patient.gender as PatientFormValues['gender']) || 'prefer_not_to_say',
                curp: patient.curp || '',
                rfc: patient.rfc || '',
                place_of_birth: patient.place_of_birth || '',
                nationality: patient.nationality || '',
                education_level: patient.education_level || '',
                religion: patient.religion || '',
                coverage_type: (patient.coverage_type as PatientFormValues['coverage_type']) || '',
                referring_physician: patient.referring_physician || '',
                email: patient.email || '',
                phone_primary: patient.phone_primary || '',
                address_line1: patient.address_line1 || '',
                address_line2: patient.address_line2 || '',
                city: patient.city || '',
                state: patient.state || '',
                postal_code: patient.postal_code || '',
                blood_type: (patient.blood_type as PatientFormValues['blood_type']) || '',
                current_status: patient.current_status || '',
                emergency_contacts: (patient.emergency_contacts && patient.emergency_contacts.length > 0)
                    ? patient.emergency_contacts
                    : [{ name: '', relationship: '', phone: '', is_primary: true }]
            })
        }
    }, [patient, form])

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'emergency_contacts'
    })

    function onSubmit(data: PatientFormValues) {
        updatePatient({
            id: patient.id,
            data: {
                ...data,
                date_of_birth: data.date_of_birth,
            } as any
        }, {
            onSuccess: () => {
                setOpen(false)
                toast({
                    title: 'Paciente actualizado',
                    description: 'La información del paciente ha sido actualizada exitosamente.',
                })
            },
            onError: (error) => {
                toast({
                    title: 'Error de actualización',
                    description: error.message || 'Hubo un error al actualizar al paciente.',
                    variant: 'destructive',
                })
            }
        })
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {trigger ? (
                <DialogTrigger asChild>
                    {trigger}
                </DialogTrigger>
            ) : controlledOpen === undefined ? (
                <DialogTrigger asChild>
                    <Button variant="outline">
                        <Edit className="mr-2 h-4 w-4" />
                        Editar
                    </Button>
                </DialogTrigger>
            ) : null}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar Paciente</DialogTitle>
                    <DialogDescription>
                        Actualiza la información personal y de contacto del paciente.
                    </DialogDescription>
                </DialogHeader>

                {/* Photo + status row */}
                <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                        {patient.photo_url ? (
                            <img
                                src={patient.photo_url}
                                alt={patient.full_name}
                                className="h-16 w-16 rounded-full object-cover border-2 border-ml-outline-variant/20"
                            />
                        ) : (
                            <div className="h-16 w-16 rounded-full bg-ml-primary flex items-center justify-center text-white text-lg font-headline italic border-2 border-ml-outline-variant/20">
                                {(patient.first_name?.[0] ?? '') + (patient.last_name?.[0] ?? '')}
                            </div>
                        )}
                        {isUploadingPhoto && (
                            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                                <Loader2 className="h-4 w-4 animate-spin text-white" />
                            </div>
                        )}
                    </div>
                    <div className="flex-1">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (!file) return
                                if (file.size > 5 * 1024 * 1024) {
                                    toast({ title: 'Archivo muy grande', description: 'La foto no puede exceder 5 MB.', variant: 'destructive' })
                                    if (fileInputRef.current) fileInputRef.current.value = ''
                                    return
                                }
                                uploadPhoto(
                                    { patientId: patient.id, file },
                                    {
                                        onSuccess: () => {
                                            toast({ title: 'Foto actualizada' })
                                            if (fileInputRef.current) fileInputRef.current.value = ''
                                        },
                                        onError: () => {
                                            toast({ title: 'Error al subir foto', variant: 'destructive' })
                                            if (fileInputRef.current) fileInputRef.current.value = ''
                                        },
                                    }
                                )
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingPhoto}
                            className="flex items-center gap-2 text-xs text-ml-primary hover:underline"
                        >
                            <Camera className="h-4 w-4" />
                            {patient.photo_url ? 'Cambiar foto' : 'Subir foto'}
                        </button>
                    </div>
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Personal Info */}
                            <FormField
                                control={form.control}
                                name="first_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nombre</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Juan" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="last_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Apellido</FormLabel>
                                        <FormControl>
                                            <Input placeholder="García López" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="date_of_birth"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Fecha de Nacimiento</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="gender"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Género</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Seleccionar género" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="male">Masculino</SelectItem>
                                                <SelectItem value="female">Femenino</SelectItem>
                                                <SelectItem value="non_binary">No binario</SelectItem>
                                                <SelectItem value="prefer_not_to_say">Prefiero no indicarlo</SelectItem>
                                                <SelectItem value="other">Otro</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="curp"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>CURP</FormLabel>
                                        <FormControl>
                                            <Input placeholder="18 caracteres" maxLength={18} className="font-mono uppercase" {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="rfc"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>RFC</FormLabel>
                                        <FormControl>
                                            <Input placeholder="13 caracteres" maxLength={13} className="font-mono uppercase" {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="current_status"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Estado Clínico Actual</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Ej: Control Prenatal, Post-quirúrgico..."
                                                maxLength={100}
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* NOM-004 identification-card demographics */}
                            <FormField
                                control={form.control}
                                name="place_of_birth"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Lugar de Nacimiento</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Ciudad, Estado" maxLength={150} {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="nationality"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nacionalidad</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Ej: Mexicana" maxLength={100} {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="education_level"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Escolaridad</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Ej: Licenciatura" maxLength={100} {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="religion"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Religión</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Opcional" maxLength={100} {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="coverage_type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tipo de Cobertura</FormLabel>
                                        <Select
                                            onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                                            value={field.value || '__none__'}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Seleccionar cobertura" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="__none__">Sin especificar</SelectItem>
                                                <SelectItem value="private">Particular (privado)</SelectItem>
                                                <SelectItem value="insurance">Aseguranza / seguro</SelectItem>
                                                <SelectItem value="institutional">Institucional (IMSS / ISSSTE)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="referring_physician"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Médico que Refiere</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Nombre y especialidad" maxLength={200} {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Contact Info */}
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Correo Electrónico</FormLabel>
                                        <FormControl>
                                            <Input type="email" placeholder="juan.garcia@ejemplo.com" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="phone_primary"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Teléfono</FormLabel>
                                        <FormControl>
                                            <Input placeholder="+52 55 0000 0000" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Blood Type */}
                            <FormField
                                control={form.control}
                                name="blood_type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tipo de Sangre</FormLabel>
                                        <Select
                                            onValueChange={(v) => field.onChange(v === '__unknown__' ? '' : v)}
                                            value={field.value || '__unknown__'}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Seleccionar tipo de sangre" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="__unknown__">Desconocido</SelectItem>
                                                <SelectItem value="A+">A+</SelectItem>
                                                <SelectItem value="A-">A-</SelectItem>
                                                <SelectItem value="B+">B+</SelectItem>
                                                <SelectItem value="B-">B-</SelectItem>
                                                <SelectItem value="AB+">AB+</SelectItem>
                                                <SelectItem value="AB-">AB-</SelectItem>
                                                <SelectItem value="O+">O+</SelectItem>
                                                <SelectItem value="O-">O-</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Address */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium">Dirección</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="address_line1"
                                    render={({ field }) => (
                                        <FormItem className="md:col-span-2">
                                            <FormLabel>Dirección Línea 1</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Calle, número exterior e interior, colonia" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="address_line2"
                                    render={({ field }) => (
                                        <FormItem className="md:col-span-2">
                                            <FormLabel>Dirección Línea 2</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Depto., piso (opcional)" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Ciudad / Municipio</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Ciudad de México" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="state"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Estado</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Jalisco" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="postal_code"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Código Postal</FormLabel>
                                            <FormControl>
                                                <Input placeholder="06600" maxLength={6} {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        {/* Emergency Contacts */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium">Contactos de Emergencia</h3>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => append({ name: '', relationship: '', phone: '', is_primary: false })}
                                >
                                    Agregar Contacto
                                </Button>
                            </div>

                            {fields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end border p-4 rounded-md bg-muted/20">
                                    <FormField
                                        control={form.control}
                                        name={`emergency_contacts.${index}.name`}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-xs">Nombre</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Nombre del Contacto" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name={`emergency_contacts.${index}.relationship`}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-xs">Relación</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="ej. Cónyuge" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="flex gap-2">
                                        <FormField
                                            control={form.control}
                                            name={`emergency_contacts.${index}.phone`}
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormLabel className="text-xs">Teléfono</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Teléfono" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        {fields.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="mb-2"
                                                onClick={() => remove(index)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {form.formState.errors.emergency_contacts && (
                                <p className="text-sm font-medium text-destructive">
                                    {form.formState.errors.emergency_contacts.message || form.formState.errors.emergency_contacts.root?.message}
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="gradient-medical hover:opacity-90 transition-opacity shadow-lg shadow-medical-500/25" disabled={isPending}>
                                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Guardar Cambios
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
